import type { RichUnit } from '../../services/api';
import type { RootScope } from '../../types';

import React from 'react';
import { Trans } from '@lingui/macro';
import { Checkbox, Form, Input, Modal, Radio, Space } from 'antd';

import { megaNumber } from '../../est/filters';
import { getService } from '../../shell';
import { notify } from '../../ui';

export type ChangePriceModalProps = {
  unit: RichUnit;
  title?: string | React.ReactElement;
  open: boolean;
  onClose(): void;
  onSubmit(newUnit: UnitPriceData): Promise<void>;
};

type FormData = {
  area_total: string;
  is_price_per_meter: '1' | '0';
  auto_exchange_rate: boolean;
  current_cost_set: string;
  current_price_set: string;
  current_currency_set: string;
  exchange_rate_declared: string;
};

export type UnitPriceData = {
  area_total: string;
  is_price_per_meter: 1 | 0;

  /**
   * current_price_set or current_cost_set depending on is_price_per_meter value
   */
  price: string;
  currency: string | null;
  exchange_rate_declared: string | null;
};

export function ChangePriceModal({
  unit,
  title = (
    <Trans id="unit.popup.change.params.title">Change area and price</Trans>
  ),
  open,
  onClose,
  onSubmit,
}: ChangePriceModalProps) {
  const [form] = Form.useForm<FormData>();
  const [submitting, setSubmitting] = React.useState(false);
  const $rootScope = getService<RootScope>('$rootScope');
  const initialValues = {
    area_total: unit.area_total,
    is_price_per_meter: unit.is_price_per_meter,
    current_cost_set: unit.current_cost_set,
    current_price_set: unit.current_price_set,
    current_currency_set: unit.current_currency_set,
    auto_exchange_rate: !unit.exchange_rate_declared,
    exchange_rate_declared: unit.exchange_rate_declared,
  };
  const isPricePerMeter =
    (Form.useWatch('is_price_per_meter', form) ||
      initialValues.is_price_per_meter) === '1';
  const autoExchange =
    Form.useWatch('auto_exchange_rate', form) ??
    initialValues.auto_exchange_rate;
  const currentUnitCost =
    Form.useWatch('current_cost_set', form) ?? initialValues.current_cost_set;
  const currentCurrency =
    Form.useWatch('current_currency_set', form) ||
    initialValues.current_currency_set;

  const isDefaultCurrency = currentCurrency === unit.company_default_currency;

  const currencyOptions: { code: string; sign: string }[] = [];

  const companyCurrency = {
    1: { code: 'UAH', sign: '₴' },
    2: { code: 'RUB', sign: '₽' },
    3: { code: 'BYN', sign: 'Br' },
    4: { code: 'VND', sign: '₫' },
    5: { code: 'KZT', sign: '₸' },
  }[$rootScope.company.country_id];

  if (companyCurrency) {
    currencyOptions.push(companyCurrency);
  }

  if (['1', '2', '3', '4', '5'].includes($rootScope.company.country_id)) {
    currencyOptions.push({ code: 'USD', sign: '$' });
  }

  // handle the case of new unit without prices
  if (initialValues.is_price_per_meter === null) {
    initialValues.is_price_per_meter = '1';
  }

  // handle the case of new unit without prices
  if (initialValues.current_currency_set === null) {
    initialValues.current_currency_set = currencyOptions[0]?.code;
  }

  return (
    <Modal
      open={open}
      destroyOnHidden
      title={title}
      okText={<Trans id="unit.popup.save">Save</Trans>}
      okButtonProps={{
        loading: submitting,
      }}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      {open && (
        <Form
          form={form}
          clearOnDestroy
          layout="vertical"
          initialValues={initialValues}
          onValuesChange={(changed, values) => {
            // recalculate prices on area change or when one of prices updated

            const updateCurrentCost = () => {
              form.setFieldValue(
                'current_cost_set',
                Math.round(
                  (Number(values.area_total) || 0) *
                    (Number(values.current_price_set) || 0) *
                    100,
                ) / 100,
              );
            };

            const updateCurrentPrice = () => {
              form.setFieldValue(
                'current_price_set',
                Math.round(
                  ((Number(values.current_cost_set) || 0) /
                    (Number(values.area_total) || 1)) *
                    100,
                ) / 100,
              );
            };

            if ('area_total' in changed) {
              if (values.is_price_per_meter === '1') {
                updateCurrentCost();
              } else {
                updateCurrentPrice();
              }
            }

            if ('current_price_set' in changed) {
              updateCurrentCost();
            }

            if ('current_cost_set' in changed) {
              updateCurrentPrice();
            }
          }}
          onFinish={async ({ auto_exchange_rate, ...values }) => {
            setSubmitting(true);

            try {
              await onSubmit({
                area_total: values.area_total,
                is_price_per_meter: values.is_price_per_meter === '1' ? 1 : 0,
                price:
                  values.is_price_per_meter === '1'
                    ? values.current_price_set
                    : values.current_cost_set,
                currency:
                  values.current_currency_set === unit.company_default_currency
                    ? null
                    : values.current_currency_set,
                exchange_rate_declared:
                  auto_exchange_rate ||
                  values.current_currency_set === unit.company_default_currency
                    ? null
                    : values.exchange_rate_declared,
              });
            } catch (error) {
              notify.error('Error changing unit price', error, {
                values,
                unit,
              });
            }

            setSubmitting(false);
          }}
        >
          <Form.Item<FormData['area_total']>
            name="area_total"
            label={
              <Trans id="unit.popup.change.params.edit.area">
                Edit total area
              </Trans>
            }
            required
            rules={[{ required: true }, { pattern: /^[0-9]+(\.[0-9]{1,2})?$/ }]}
          >
            <Input
              type="number"
              placeholder="e.g. 115.51"
              step="1"
              min="0"
              addonAfter={
                <>
                  <Trans id="unit.popup.meter">m</Trans>
                  <sup>2</sup>
                </>
              }
            />
          </Form.Item>

          <Form.Item<FormData['is_price_per_meter']>
            name="is_price_per_meter"
            label={
              <Trans id="unit.popup.change.params.edit.price">Edit price</Trans>
            }
          >
            <Radio.Group
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
              options={[
                {
                  value: '1',
                  label: (
                    <Trans id="unit.popup.change.params.edit.price.per.meter">
                      set price per sqm
                    </Trans>
                  ),
                },
                {
                  value: '0',
                  label: (
                    <Trans id="unit.popup.change.params.edit.price.per.unit">
                      set unit total cost
                    </Trans>
                  ),
                },
              ]}
            />
          </Form.Item>

          <Space>
            <Form.Item<FormData['current_price_set']>
              name="current_price_set"
              label={
                <Trans id="unit.popup.change.params.edit.per.sqm">
                  Price of sqm
                </Trans>
              }
              required
              rules={[
                { required: true },
                { pattern: /^[0-9]+(\.[0-9]{1,2})?$/ },
              ]}
            >
              <Input
                type="number"
                step="100"
                min="0"
                disabled={!isPricePerMeter}
              />
            </Form.Item>

            <Form.Item<FormData['current_cost_set']>
              name="current_cost_set"
              label={
                <Trans id="unit.popup.change.params.edit.cost">
                  Total cost
                </Trans>
              }
              required
              rules={[
                { required: true },
                { pattern: /^[0-9]+(\.[0-9]{1,2})?$/ },
              ]}
            >
              <Input
                type="number"
                step="1000"
                min="0"
                disabled={isPricePerMeter}
              />
            </Form.Item>

            <span className="g-color-primary" style={{ lineHeight: '35px' }}>
              ~ {$rootScope.getCurrencySymbol(currentCurrency)}{' '}
              {megaNumber(Number(currentUnitCost) || 0)}
            </span>
          </Space>

          <Space>
            <Form.Item<
              FormData['current_currency_set']
            > name="current_currency_set">
              <Radio.Group
                optionType="button"
                options={currencyOptions.map((item) => ({
                  value: item.code,
                  label: item.sign,
                }))}
              />
            </Form.Item>

            {!isDefaultCurrency && (
              <div>
                <Form.Item<FormData['auto_exchange_rate']>
                  name="auto_exchange_rate"
                  valuePropName="checked"
                >
                  <Checkbox>
                    <Trans id="unit.popup.change.params.edit.auto.exchange.rate">
                      Auto exchange rate
                    </Trans>
                  </Checkbox>
                </Form.Item>
              </div>
            )}
          </Space>

          {!isDefaultCurrency && !autoExchange && (
            <Form.Item<FormData['exchange_rate_declared']>
              name="exchange_rate_declared"
              label={
                <Trans id="unit.popup.change.params.edit.exchange.rate">
                  Exchange rate
                </Trans>
              }
              required
              rules={[
                { required: true },
                { pattern: /^[0-9]+(\.[0-9]{1,4})?$/ },
              ]}
            >
              <Input
                type="number"
                autoFocus
                step="0.0001"
                min="0"
                addonAfter={
                  <Trans id="unit.popup.change.params.edit.for.1usd">
                    for 1 USD
                  </Trans>
                }
              />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
}
